import { FC, HTMLAttributes } from "react";
import clsx from "clsx";

// ----------------------------------------------------------------------------
// Full-width dark strip with N equally-spaced stat columns rendered directly
// under the hero. Each cell shows a large vivid number on top and a small
// caption underneath. Renders flush with the hero (no top margin) and with
// no rounded corners so it composes visually with TrustedByStripSection
// below it into one continuous proof band.
// ----------------------------------------------------------------------------

export type IStatItem = {
  name?: string;
  number?: string;
};

export type IStatsStripSectionProps = HTMLAttributes<HTMLElement> & {
  stats?: IStatItem[];
};

const StatsStripSection: FC<IStatsStripSectionProps> = (props) => {
  const { stats, className, ...other } = props;

  if (!stats || stats.length === 0) return null;

  return (
    <section
      className={clsx("border-t border-white/10 bg-black-950", className)}
      {...other}
    >
      <div className="grid grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
        {stats.map((stat, idx) => (
          // Padding scales down at the narrow end — `px-6` (24px) on each
          // side was eating ~48px of a ~160px column, leaving the "1,200"
          // glyphs nowhere to go and they clipped. `px-4` on mobile keeps
          // the cell breathable for 4-character numbers.
          <div
            key={idx}
            className="px-4 py-7 sm:px-6 md:px-10 md:py-12 lg:px-14"
          >
            {stat.number && (
              // Fluid: ~32px on the narrowest phone → 72px on desktop.
              // Replaces the old text-5xl/6xl/7xl ladder which started
              // at 48px and clipped 4-character numbers ("1,200") on
              // any viewport under ~375px. min-w-0 + break-words on the
              // wrapper would only force ugly mid-number wraps, so the
              // right fix is to shrink the type itself on small screens.
              <div className="font-bricolage-grotesque bg-primary-gradient bg-clip-text text-[clamp(2rem,8vw+0.5rem,4.5rem)] font-semibold leading-none text-transparent">
                {stat.number}
              </div>
            )}
            {stat.name && (
              <div className="mt-3 text-[10px] font-normal uppercase tracking-wider text-white/70 sm:text-xs md:text-sm">
                {stat.name}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default StatsStripSection;
